import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import firebase from 'firebase/app';
import { firstValueFrom, Observer, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserRoles } from 'src/app/dictionaries/UserRoles';

import { OptionChooserComponent } from './../components/ui/optionChooser/optionChooser.component';
import { AuthService } from './../services/auth.service';
import { FirebaseUtilitiesService } from './../services/firebase-utilities.service';
import { UIMessagingService } from './../services/uimessaging.service';
import { UtilsService } from './../services/utils.service';
import { SmsService } from './../services/sms.service';
import { TwoFactorAuthRequestDialogComponent } from './../components/two-factor-auth-request/two-factor-auth-request.dialog';
import { TwoFactorAuthSetupDialogComponent } from './../components/two-factor-auth-setup-dialog-component/two-factor-auth-setup-dialog.component';
import { TwoFactorSignInModalComponent } from './../components/two-factor-sign-in-modal/two-factor-sign-in-modal.component';
import { DialogService } from './../services/dialog.service';

@Injectable({
  providedIn: 'root'
})
export class TwoFactorAuthenticationService {
  public email: string;
  dialogConfig: MatDialogConfig;
  dialofRef: any;
  methodUserChoice = new Subject();
  methodUserChoiceObserver: Observer<string> = {
    next: val => this.sendCode(val),
    error: error => console.error(error),
    complete: () => console.log('Completed')
  };

  constructor(
    private dialog_$: DialogService,
    private firebase_$: FirebaseUtilitiesService,
    private utils_$: UtilsService,
    private auth_$: AuthService,
    private sms_$: SmsService,
    private http: HttpClient,
    private uiMessaging_$: UIMessagingService
  ) { }

  async sendCode(method: string) {
    console.log('sendCode');
    await this.send2faCODE(method, this.email);
    this.promptToVerifyCode(method, this.email);
  }

  getUserChoice() {
    this.methodUserChoice.subscribe(this.methodUserChoiceObserver);

    const dialogConfig: MatDialogConfig = {
      width: '320px',
      disableClose: true,
      backdropClass: 'blur-backdrop',
      data: {
        title: 'Two-Factor Authentication',
        message: 'Please choose a method to get your <b>verification code</b>. This step is required to access the app:',
        options: [
          { name: 'Email', value: 'email' },
          { name: 'SMS', value: 'sms' }
        ],
        mode: 'button',
        showLogout: true
      }
    };

    const dialog = this.dialog_$.open(OptionChooserComponent, dialogConfig);
    dialog.afterClosed().subscribe(v => {
      if (v === 'logout') {
        this.auth_$.logout();
        window.location.reload();
        return;
      }
      this.setUserChoice(v || null);
    });
  }

  setUserChoice(value: string) {
    if (!value) {
      console.error(`value:${value} is null or undefined`);
      return;
    } else {
      this.methodUserChoice.next(value);
      this.methodUserChoice.complete();
      this.methodUserChoice.unsubscribe();
    }
  }

  async handleTwoFactorSignIn(email) {
    if (!environment.twoFactorAuthEnabled) return;
    this.email = email;
    const twoFactorData = await this.getTwoFactorAuthData(email);
    const { twoFactorSet, twoFactorEnabled } = twoFactorData;
    if (twoFactorSet) {
      try {
        if (twoFactorEnabled) {
          this.getUserChoice();
        }
      } catch (error) {
        this.auth_$.errorAuth.next(error.message);
      }
    } else {
      const role = this.auth_$.userData.getValue()?.['role'];
      if (role === UserRoles.owner) {
        this.twoFactorSetup(email);
      } else {
        this.promptForCode2FASetup(email);
      }
    }
  }

  promptToVerifyCode(twoFactorMethod: any, email: string) {
    // PHI sanitized: don't log email
    this.dialogConfig = {
      width: '450px',
      disableClose: true,
      backdropClass: 'blur-backdrop',
      data: {
        email: email,
        method: twoFactorMethod,
        title: `Two-factor Authentication`,
        message: `Please enter the code you received.`
      }
    };
    this.dialog_$
      .open(TwoFactorSignInModalComponent, this.dialogConfig)
      .afterClosed()
      .subscribe(res => {
        // Do something with res
        console.log(res);
        switch (res) {
          case 'cancel':
            this.auth_$.logout();
            window.location.reload();
            break;
          default:
            break;
        }
      });
  }

  promptForCode2FASetup(email) {
    this.dialog_$
      .open(TwoFactorAuthRequestDialogComponent, { disableClose: true, width: '400px', backdropClass: 'blur-backdrop' })
      .afterClosed()
      .subscribe(res => {
        if (res.answer === 0) {
          // this.handleLogin();
        }
        this.handleTwoFactorAuthRequestAnswer(res, email);
      });
  }

  handleTwoFactorAuthRequestAnswer(res: any, email) {
    if (res) res.answer ? this.twoFactorSetup(email) : this.noTwoFactorAuthSetup(email);
  }

  noTwoFactorAuthSetup(email: string) {
    const role = this.auth_$.userData.getValue()?.['role'];
    if (role === UserRoles.owner) return;
    this.firebase_$.setNoFactorAuthSetup(email, 'no-method', false, true);
  }

  twoFactorSetup(email) {
    this.dialogConfig = { disableClose: true, width: '450px' };
    this.dialogConfig.data = {
      email: email,
      title: 'Two-factor Authentication Setup',
      message: 'We need to get and validate your phone number',
      phoneNumber: this.auth_$.userData.getValue()['phoneNumber'] || null
    };
    const dialogRef = this.dialog_$.open(TwoFactorAuthSetupDialogComponent, this.dialogConfig);
    dialogRef.afterClosed().subscribe({
      next: res => {
        console.log('TwoFactorAuthSetupDialogComponent RES', res);
        if (res && res.answer && res.answer === 1) dialogRef.close('1');
        else if (res === 'cancel') console.log('The user has cancelled the 2fa setup.');
      }
    });
  }

  async send2faCODE(twoFactorMethod: any, email: string) {
    // PHI sanitized: don't log email

    switch (twoFactorMethod) {
      case 'email':
        await this.confirmEmail(email);
        break;
      case 'sms':
        await this.sendCodeBySMS(email);
        break;
      default:
        break;
    }
  }

  /**
   * Using the @param email the information of Two-factor authentication is retrieved.
   * Uses the merged auth-preSignIn Cloud Function (same call as account restriction check).
   */
  async getTwoFactorAuthData(email: string) {
    try {
      const url = `${environment.constants.cloudfunctionsURL}auth-preSignIn`;
      const twoFactorData = await firstValueFrom(this.http.post<{
        twoFactorSet: boolean;
        twoFactorEnabled: boolean;
        twoFactorMethod: string;
      }>(url, { email }));
      return twoFactorData;
    } catch (err) {
      const status = err?.status ?? (err && (err as any).status);
      const isNetworkOrCors = status === 0 || (err && (err as any).message === 'Http failure response');
      if (isNetworkOrCors) {
        console.warn('2FA data unavailable (network/CORS). Proceeding without 2FA prefill.');
      } else {
        console.error('Error fetching 2FA data:', err);
      }
      return { twoFactorSet: null, twoFactorEnabled: null, twoFactorMethod: null };
    }
  }

  async sendCodeBySMS(email: string) {
    // Try email-keyed doc first (legacy users); fall back to UID-keyed doc (facility/owner users)
    let sp = await firebase.firestore().collection('users').doc(email).get();
    if (!sp.exists) {
      const query = await firebase.firestore().collection('users').where('email', '==', email).limit(1).get();
      if (!query.empty) sp = query.docs[0];
    }
    const rawPhone = sp?.data()?.['phoneNumber'];

    if (!rawPhone) {
      this.uiMessaging_$.toastMessage('No phone number found on your account. Please set up 2FA again.', 'Error');
      return false;
    }

    // Normalize to E.164 format: strip non-digits, prepend +1 for US numbers
    const digits = String(rawPhone).replace(/\D/g, '');
    const phoneNumber = digits.startsWith('1') ? `+${digits}` : `+1${digits}`;

    const code = this.utils_$.generateValidationCode();
    await this.firebase_$.setUserCode(code, 'phone', email);

    // SEND SMS.
    const msg = `NuagedX has sent you a CODE: ${code}`;
    this.sms_$.sendSMS(msg, phoneNumber).subscribe({
      next: res => {
        if (res['data']['status'] === 200) {
          console.log('RES:', res);
          // this.promptForCode();
        }
      },
      complete: () => {
        console.log('Completed SMS sending');
        // this.promptForCode();
      },
      error: err => {
        console.error(err);
      }
    });
  }

  async confirmEmail(email, type?: number) {
    // PHI sanitized: don't log email or code
    const code = this.utils_$.generateValidationCode();
    this.firebase_$.setUserCode(code, 'email', email);
    const url = `${environment.constants.cloudfunctionsURL}email-simpleMail`;
    const dest = email;

    // Use Firebase Auth token instead of hardcoded key
    let authToken = '';
    try {
      const currentUser = firebase.auth().currentUser;
      if (currentUser) {
        authToken = await currentUser.getIdToken();
      }
    } catch (err) {
      console.error('Failed to get auth token');
    }

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`
      })
    };

    const body =
      `<h2>Please confirm</h2>` +
      `<p>Use the following code to confirm your Two-factor Authentication (2FA):</p>` +
      `<strong>${code}</strong>` +
      `<p>There is a prompt input where you have to paste/type this code.</p>`;
    const subject = `NuagedX has sent you a verification code`;

    return this.http.post(url, { dest, body, subject }, httpOptions).subscribe({
      next: res => {
        if (res['data']['status'] === 200) {
          console.log('2FA email sent successfully');
        }
      },
      complete: () => {
        console.log('2FA email request completed');
      },
      error: err => {
        console.error('2FA email failed');
      }
    });
  }
}
